import { createCallControlHandler } from './utils';
import {
  DeviceSelectorVideo,
  OwnCapability,
  PermissionNotification,
  Restricted,
  useCallStateHooks,
  useI18n,
  useRequestPermission,
  WithTooltip,
} from '@stream-io/video-react-sdk';
import { useState } from 'react';
import { Video, VideoOff } from 'lucide-react';
import { CompositeButton } from './composite-button';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ToggleVideoPublishingButton = (props: any) => {
  const { t } = useI18n();
  const {
    caption,
    Menu = <DeviceSelectorVideo visualType="list" />,
    menuPlacement = 'top',
    onMenuToggle,
    ...restCompositeButtonProps
  } = props;

  const { hasPermission, requestPermission, isAwaitingPermission } = useRequestPermission(
    OwnCapability.SEND_VIDEO
  );

  const { useCameraState, useCallSettings } = useCallStateHooks();
  const { camera, optimisticIsMute, hasBrowserPermission } = useCameraState();
  const callSettings = useCallSettings();
  const isPublishingVideoAllowed = callSettings?.video.enabled;
  const [tooltipDisabled, setTooltipDisabled] = useState(false);
  const handleClick = createCallControlHandler(props, async () => {
    if (!hasPermission) {
      await requestPermission();
    } else {
      await camera.toggle();
    }
  });

  return (
    <Restricted requiredGrants={[OwnCapability.SEND_VIDEO]}>
      <PermissionNotification
        permission={OwnCapability.SEND_VIDEO}
        isAwaitingApproval={isAwaitingPermission}
        messageApproved={t('You can now share your video.')}
        messageAwaitingApproval={t('Awaiting for an approval to share your video.')}
        messageRevoked={t('You can no longer share your video.')}
      >
        <WithTooltip
          title={
            !hasPermission
              ? t('You have no permission to share your video')
              : !hasBrowserPermission
                ? t('Check your browser video permissions')
                : !isPublishingVideoAllowed
                  ? t('Video publishing is disabled by the system')
                  : caption || t('Video')
          }
          tooltipDisabled={tooltipDisabled}
        >
          <CompositeButton
            active={optimisticIsMute}
            caption={caption}
            variant="secondary"
            disabled={!hasBrowserPermission || !hasPermission || !isPublishingVideoAllowed}
            onClick={handleClick}
            Menu={Menu}
            menuPlacement={menuPlacement}
            menuOffset={16}
            {...restCompositeButtonProps}
            onMenuToggle={(shown) => {
              setTooltipDisabled(shown);
              onMenuToggle?.(shown);
            }}
          >
            {optimisticIsMute ? (
              <VideoOff className="w-5 h-5 text-accent" />
            ) : (
              <Video className="w-5 h-5 text-accent" />
            )}
            {(!hasBrowserPermission || !hasPermission || !isPublishingVideoAllowed) && (
              <span className="str-video__no-media-permission">!</span>
            )}
          </CompositeButton>
        </WithTooltip>
      </PermissionNotification>
    </Restricted>
  );
};
